import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../lib/api.js';
import { useCurrentUser } from '../../hooks/useCurrentUser.js';
import { PrintBookingsButton } from './PrintBookingsButton.jsx';
import { PageHeader, LoadingPage, ErrorPage, StatusPill } from '../../components/ui/Feedback.jsx';
import { Search, Calendar, X, ChevronLeft, ChevronRight, MapPin, Clock, User, Car, Plus } from 'lucide-react';
import { ListModeToggle } from './QuotesListPage.jsx';

// =============================================================================
// BookingsListPage (Round 7d.2.1)
// =============================================================================
// Top-level /bookings route. Dispatcher's main hub for working through bookings.
//
// Layout:
//   - Page header with title + booking count
//   - "New booking" CTA row (added 7d.3.1)
//   - Filter sidebar on the left (collapsible on mobile)
//   - Results table on the right
//   - Pagination footer
//
// Filters reflected in URL query string so dispatcher can bookmark/share views
// (e.g. ?date_preset=today&status=confirmed,allocated). On load, we hydrate
// state from the URL.
//
// Default load: today's bookings, all statuses, sorted by pickup time ascending.
// Click a row → navigate to /bookings/:id (slice 7d.2.2 — currently a 404 stub
// until that page exists).
// =============================================================================

function cn(...classes) {
    return classes.filter(Boolean).join(' ');
}

// All status values the backend recognises, with display labels and pill colors.
const STATUS_CONFIG = {
    pending_payment: { label: 'Pending payment', pillStatus: 'warning' },
    confirmed:  { label: 'Confirmed',  pillStatus: 'info'    },
    allocated:  { label: 'Allocated',  pillStatus: 'neutral' },  // internal-only, not yet sent to chauffeur
    dispatched: { label: 'Dispatched', pillStatus: 'info'    },  // sent to chauffeur, visible in portal
    en_route:   { label: 'En route',   pillStatus: 'warning' },
    arrived:    { label: 'Arrived',    pillStatus: 'warning' },
    on_journey: { label: 'On journey', pillStatus: 'warning' },
    completed:  { label: 'Completed',  pillStatus: 'success' },
    cancelled:  { label: 'Cancelled',  pillStatus: 'neutral' },
    no_show:    { label: 'No show',    pillStatus: 'danger'  },
};
// All status keys — used to default the filter to "everything ticked".
// Semantics: all-checked is identical to none-checked from the backend's POV
// (no WHERE clause either way). UI defaults to all-checked because that's
// less mental work for the dispatcher to reason about ("I see everything").
const ALL_STATUSES = Object.keys(STATUS_CONFIG);
const STATUS_LABELS = Object.fromEntries(Object.entries(STATUS_CONFIG).map(([k, v]) => [k, v.label]));

const DATE_PRESETS = [
    { value: 'today',        label: 'Today'         },
    { value: 'tomorrow',     label: 'Tomorrow'      },
    { value: 'this_week',    label: 'This week'     },
    { value: 'last_week',    label: 'Last week'     },
    { value: 'last_30_days', label: 'Last 30 days'  },
    { value: 'last_month',   label: 'Last month'    },
    { value: 'last_quarter', label: 'Last quarter'  },
];

// Mobile-only subset of date presets — fewer options to fit on a phone screen.
// Module-scoped so it isn't recreated on every MobileFilterBar render.
const MOBILE_DATE_PRESETS = [
    { value: 'today',     label: 'Today'     },
    { value: 'tomorrow',  label: 'Tomorrow'  },
    { value: 'this_week', label: 'This week' },
];

// Resolve a preset name to actual {from, to} ISO date strings (YYYY-MM-DD).
// Mirrors the backend's date_preset resolution in routes/bookings.js so the
// dates shown in the UI inputs always match what the backend will filter by.
//
// Server-side resolution is still authoritative — this is purely so the user
// SEES the date range that's being applied. If the two ever drift, the bug
// will show up as "the box says X but the data shows Y", which is loud.

// Shift both date_from and date_to by N days. Used by the custom-range
// arrow buttons to scrub forward/backward through history one day at a time
// without re-entering dates manually. Clears the active preset since we're
// now in custom-range territory.
function shiftDateWindow(deltaDays, filters, updateFilters) {
    if (!filters.date_from || !filters.date_to) return;

    const fmt = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    // Parse YYYY-MM-DD as local midnight (not UTC) — avoids timezone drift
    // when shifting near DST transitions.
    const [yFrom, mFrom, dFrom] = filters.date_from.split('-').map(Number);
    const [yTo, mTo, dTo] = filters.date_to.split('-').map(Number);
    const from = new Date(yFrom, mFrom - 1, dFrom);
    const to = new Date(yTo, mTo - 1, dTo);

    from.setDate(from.getDate() + deltaDays);
    to.setDate(to.getDate() + deltaDays);

    updateFilters({
        date_from: fmt(from),
        date_to: fmt(to),
        date_preset: '',
        offset: 0,
    });
}
// Custom range edits — keep the range valid. Picking a From later than To
// pulls To along (and vice versa), so a single date needs one edit, not two.
function setFromDate(value, filters, updateFilters) {
    const changes = { date_from: value, date_preset: '', offset: 0 };
    if (value && (!filters.date_to || value > filters.date_to)) changes.date_to = value;
    updateFilters(changes);
}

function setToDate(value, filters, updateFilters) {
    const changes = { date_to: value, date_preset: '', offset: 0 };
    if (value && (!filters.date_from || value < filters.date_from)) changes.date_from = value;
    updateFilters(changes);
}

function presetToDates(preset) {
    const today = new Date();
    const fmt = (d) => {
        // Use local-date components rather than toISOString() (which is UTC) —
        // for a UK user at 23:00 BST, toISOString() returns tomorrow's date.
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    if (preset === 'today') {
        return { from: fmt(today), to: fmt(today) };
    }
    if (preset === 'tomorrow') {
        const t = new Date(today); t.setDate(t.getDate() + 1);
        return { from: fmt(t), to: fmt(t) };
    }
    if (preset === 'this_week') {
        const day = today.getDay() || 7; // Sun=0 → 7
        const monday = new Date(today); monday.setDate(today.getDate() - (day - 1));
        const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
        return { from: fmt(monday), to: fmt(sunday) };
    }
    if (preset === 'this_month') {
        const first = new Date(today.getFullYear(), today.getMonth(), 1);
        const last  = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return { from: fmt(first), to: fmt(last) };
    }
    if (preset === 'this_year') {
        return { from: `${today.getFullYear()}-01-01`, to: `${today.getFullYear()}-12-31` };
    }
    
    if (preset === 'last_week') {
        const day = today.getDay() || 7;
        const thisMonday = new Date(today); thisMonday.setDate(today.getDate() - (day - 1));
        const lastMonday = new Date(thisMonday); lastMonday.setDate(thisMonday.getDate() - 7);
        const lastSunday = new Date(lastMonday); lastSunday.setDate(lastMonday.getDate() + 6);
        return { from: fmt(lastMonday), to: fmt(lastSunday) };
    }
     if (preset === 'last_30_days') {
        const start = new Date(today); start.setDate(today.getDate() - 30);
        return { from: fmt(start), to: fmt(today) };
    }
    if (preset === 'last_month') {
        const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const last  = new Date(today.getFullYear(), today.getMonth(), 0);
        return { from: fmt(first), to: fmt(last) };
    }
    if (preset === 'last_quarter') {
        const currentQuarter = Math.floor(today.getMonth() / 3);
        const lastQuarterStartMonth = (currentQuarter - 1) * 3;
        const isNewYear = currentQuarter === 0;
        const year = isNewYear ? today.getFullYear() - 1 : today.getFullYear();
        const startMonth = isNewYear ? 9 : lastQuarterStartMonth;
        const first = new Date(year, startMonth, 1);
        const last = new Date(year, startMonth + 3, 0);
        return { from: fmt(first), to: fmt(last) };
    }
    return { from: '', to: '' };
}

// Canonical vehicle class labels — must match VEHICLE_NAMES in the booking
// engine plugin (lanz-booking-engine_37_4.php line ~1181) so dispatcher and
// engine speak the same language. The 'test' vehicle is excluded — it's only
// for development testing, not real operations.
const VEHICLE_CLASSES = [
    { value: '',         label: 'Any class'         },
    { value: 'business', label: 'Business Class'    },
    { value: 'first',    label: 'First Class'       },
    { value: 'suv',      label: 'First Class SUV'   },
    { value: 'xl',       label: 'Business Class XL' },
];

// Read filters from URL on mount; provide a setter that also updates the URL.
function useUrlFilters() {
    // We use React Router's setSearchParams (not window.history.replaceState)
    // to update URL state. Critical bug fix: previously we called
    // window.history.replaceState directly, which updated the URL bar but
    // NOT React Router's internal history. Result was that navigating to a
    // booking and hitting back would forget the filters because RR popped
    // to its own pre-filter snapshot. setSearchParams keeps RR aware of
    // every URL change.
    //
    // Initial read still uses window.location.search so it only runs once
    // on mount (avoids re-init on every searchParams change).
    const [, setSearchParams] = useSearchParams();

    const initial = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        const get = (k) => params.get(k) || '';

        // Status default-all-checked behaviour:
        //   - URL has no 'status' param at all → default to all 8 checked
        //   - URL has 'status=confirmed,allocated' → just those two
        //   - URL has 'status=' (empty value) → zero checked (user explicitly cleared)
        // Backend treats both "all 8" and "zero" as "no filter" (returns everything),
        // so the only practical difference is what the checkboxes look like on load.
        // Default: no statuses checked (treated as "show all" by backend).
        // Previously defaulted to all-checked which made it tedious to filter
        // to just one status — required unticking 7 boxes first.
        const statusFromUrl = params.has('status')
            ? get('status').split(',').filter(Boolean)
            : [];

        // Date initial state: presets resolve to actual dates so the date input
        // boxes show the range being filtered. Logic:
        //   - URL has date_preset → use it, resolve to its dates (radio checked)
        //   - URL has explicit dates only (no preset) → treat as custom range
        //   - URL has neither → default to 'today' preset
        const urlPreset    = get('date_preset');
        const explicitFrom = get('date_from');
        const explicitTo   = get('date_to');
        let dateFromInit, dateToInit, presetInit;
        if (urlPreset) {
            // Preset wins — resolve to fresh dates (in case 'today' has rolled over
            // since the URL was bookmarked).
            const resolved = presetToDates(urlPreset);
            dateFromInit = resolved.from;
            dateToInit   = resolved.to;
            presetInit   = urlPreset;
        } else if (explicitFrom || explicitTo) {
            // Custom range — no preset selected
            dateFromInit = explicitFrom;
            dateToInit   = explicitTo;
            presetInit   = '';
        } else {
            // Fresh page load with no URL params — default to today
            const resolved = presetToDates('today');
            dateFromInit = resolved.from;
            dateToInit   = resolved.to;
            presetInit   = 'today';
        }

        return {
            date_preset:     presetInit,
            date_from:       dateFromInit,
            date_to:         dateToInit,
            status:          new Set(statusFromUrl),
            booking_ref:     get('booking_ref'),
            invoice_number:  get('invoice_number'),
            customer_search: get('customer_search'),
            customer_id:     get('customer_id'),
            chauffeur_id:    get('chauffeur_id'),
            vehicle_id:      get('vehicle_id'),
            vehicle_class:   get('vehicle_class'),
            pickup_area:     get('pickup_area'),
            dropoff_area:    get('dropoff_area'),
            passenger_name:  get('passenger_name'),
            passenger_phone: get('passenger_phone'),
            journey_type:    get('journey_type') || 'any',
            invoice_status:  get('invoice_status') || 'all',
            search:          get('search'),
            sort:            get('sort') || 'pickup_asc',
            offset:          parseInt(get('offset') || '0', 10),
            limit:           parseInt(get('limit') || '50', 10),
        };
    }, []);

    const [filters, setFilters] = useState(initial);

    // Sync to URL whenever filters change. Keeps page bookmarkable.
    const update = (changes) => {
        const next = { ...filters, ...changes };
        setFilters(next);
        const params = new URLSearchParams();
        // Date filters: always include preset when one is active so reload can
        // restore the radio's checked state. Date_from/to also travel along so
        // the inputs show the resolved range without re-computation.
        if (next.date_preset) params.set('date_preset', next.date_preset);
        if (next.date_from)   params.set('date_from',   next.date_from);
        if (next.date_to)     params.set('date_to',     next.date_to);
        // Status: omit from URL when all 8 are checked (default state). When user
        // explicitly clears all (zero checked), set status= to an empty value so
        // we can distinguish "default" from "user cleared" on next page load.
        // Status URL: omit when no statuses checked (default = show all).
        // Send the exact list when 1+ statuses are checked.
        if (next.status.size > 0 && next.status.size < ALL_STATUSES.length) {
            params.set('status', [...next.status].join(','));
        }
        if (next.booking_ref)     params.set('booking_ref',     next.booking_ref);
        if (next.invoice_number)  params.set('invoice_number',  next.invoice_number);
        if (next.customer_search) params.set('customer_search', next.customer_search);
        if (next.customer_id)     params.set('customer_id',     next.customer_id);
        if (next.chauffeur_id)    params.set('chauffeur_id',    next.chauffeur_id);
        if (next.vehicle_id)      params.set('vehicle_id',      next.vehicle_id);
        if (next.vehicle_class)   params.set('vehicle_class',   next.vehicle_class);
        if (next.pickup_area)     params.set('pickup_area',     next.pickup_area);
        if (next.dropoff_area)    params.set('dropoff_area',    next.dropoff_area);
        if (next.passenger_name)  params.set('passenger_name',  next.passenger_name);
        if (next.passenger_phone) params.set('passenger_phone', next.passenger_phone);
       if (next.journey_type && next.journey_type !== 'any') params.set('journey_type', next.journey_type);
        if (next.invoice_status && next.invoice_status !== 'all') params.set('invoice_status', next.invoice_status);
        if (next.search)          params.set('search',          next.search);
        if (next.sort && next.sort !== 'pickup_asc') params.set('sort', next.sort);
        if (next.offset > 0)      params.set('offset',          String(next.offset));
        if (next.limit !== 50)    params.set('limit',           String(next.limit));
        // Use replace:true so each filter tweak doesn't create a history
        // entry — would make the Back button useless. The current URL is
        // still correct when the user navigates to a booking detail, so
        // Back from there returns to this filtered view.
        setSearchParams(params, { replace: true });
    };

    const reset = () => {
        const today = presetToDates('today');
        update({
            date_preset: 'today', date_from: today.from, date_to: today.to,
            status: new Set(), booking_ref: '', invoice_number: '', customer_search: '',
            customer_id: '',
            chauffeur_id: '', vehicle_id: '', vehicle_class: '', pickup_area: '', dropoff_area: '',
            passenger_name: '', passenger_phone: '', journey_type: 'any',
            invoice_status: 'all',
            search: '', sort: 'pickup_asc', offset: 0,
        });
    };

    return [filters, update, reset];
}

// Build the query string the backend expects from filters state.
function buildQueryString(filters) {
    const params = new URLSearchParams();
    // Always send explicit dates if present — backend uses these as authoritative.
    // Preset is also sent for backend's own date_preset resolution path (used when
    // explicit dates aren't present, e.g. via direct API call), but with our UI
    // we always send both so the explicit dates win.
    if (filters.date_preset) params.set('date_preset', filters.date_preset);
    if (filters.date_from)   params.set('date_from',   filters.date_from);
    if (filters.date_to)     params.set('date_to',     filters.date_to);
    if (filters.status.size > 0) params.set('status', [...filters.status].join(','));
    if (filters.booking_ref)     params.set('booking_ref',     filters.booking_ref);
    if (filters.invoice_number)  params.set('invoice_number',  filters.invoice_number);
    if (filters.customer_search) params.set('customer_search', filters.customer_search);
    if (filters.customer_id)     params.set('customer_id',     filters.customer_id);
    if (filters.chauffeur_id)    params.set('chauffeur_id',    filters.chauffeur_id);
    if (filters.vehicle_id)      params.set('vehicle_id',      filters.vehicle_id);
    if (filters.vehicle_class)   params.set('vehicle_class',   filters.vehicle_class);
    if (filters.pickup_area)     params.set('pickup_area',     filters.pickup_area);
    if (filters.dropoff_area)    params.set('dropoff_area',    filters.dropoff_area);
    if (filters.passenger_name)  params.set('passenger_name',  filters.passenger_name);
    if (filters.passenger_phone) params.set('passenger_phone', filters.passenger_phone);
    if (filters.journey_type && filters.journey_type !== 'any') params.set('journey_type', filters.journey_type);
    if (filters.invoice_status && filters.invoice_status !== 'all') params.set('invoice_status', filters.invoice_status);
    if (filters.search) params.set('search', filters.search);
    if (filters.sort)   params.set('sort',   filters.sort);
    params.set('limit',  String(filters.limit));
    params.set('offset', String(filters.offset));
    return params.toString();
}

// Format pickup datetime as "Mon 27 Apr · 14:30"
//
// `date` arrives as one of: a Date object (from pg with date-parsing on),
// an ISO timestamp string ('2026-04-26T00:00:00.000Z'), or a plain YYYY-MM-DD
// string. We normalise to YYYY-MM-DD first, then build the timestamp.
function formatPickup(date, time) {
    if (!date) return '—';

    // Normalise to YYYY-MM-DD regardless of input shape.
    let dateStr;
    if (date instanceof Date) {
        // Use UTC components — pg returns date-only fields as midnight UTC, and
        // the local conversion would shift days at certain timezones.
        const y = date.getUTCFullYear();
        const m = String(date.getUTCMonth() + 1).padStart(2, '0');
        const d = String(date.getUTCDate()).padStart(2, '0');
        dateStr = `${y}-${m}-${d}`;
    } else if (typeof date === 'string') {
        // ISO timestamp ('2026-04-26T00:00:00.000Z') or plain date — slice first 10 chars.
        dateStr = date.slice(0, 10);
    } else {
        return '—';
    }

    try {
        const d = new Date(`${dateStr}T${time || '00:00'}`);
        if (isNaN(d.getTime())) return `${dateStr} · ${time || '—'}`;
        return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' }) + ' · ' + (time || '—');
    } catch {
        return `${dateStr} · ${time || '—'}`;
    }
}
export default function BookingsListPage() {
    const api = useApi();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { isOwner } = useCurrentUser();
    const [filters, updateFilters, resetFilters] = useUrlFilters();

    // Filters with text-input fields self-debounce inside FilterInput/SearchInput
    // (300ms after typing stops). The parent's `filters` state already reflects
    // the debounced values for those fields, so we can build the query string
    // directly from it without any further debouncing layer here.
    // Add include_totals flag for owners — backend computes customer/chauffeur
    // billing totals across the filtered set. Non-owners see no totals row.

    // Lookup queries for chauffeur + vehicle filter dropdowns. Cached aggressively
    // (5min stale) — these don't change often. Both render in sidebar regardless
    // of date filter.
    const chauffeursQuery = useQuery({
        queryKey: ['drivers', 'list-for-filter'],
        queryFn: () => api.get('/drivers'),
        staleTime: 5 * 60 * 1000,
    });
    const vehiclesQuery = useQuery({
        queryKey: ['vehicles', 'list-for-filter'],
        queryFn: () => api.get('/vehicles'),
        staleTime: 5 * 60 * 1000,
    });
    const chauffeurOptions = chauffeursQuery.data?.drivers || chauffeursQuery.data || [];
    const vehicleOptions = vehiclesQuery.data?.vehicles || vehiclesQuery.data || [];

    // Scoped filter options — only chauffeurs/customers with bookings in the
    // selected date window. Keeps the dropdown relevant instead of listing the
    // whole roster. The full chauffeurOptions roster above is still used by the
    // bulk allocate modal (you allocate to anyone, not just those already in view).
    const facetsQuery = useQuery({
        queryKey: ['bookings', 'facets', filters.date_from, filters.date_to],
        queryFn: () => api.get(`/bookings/facets?date_from=${encodeURIComponent(filters.date_from || '')}&date_to=${encodeURIComponent(filters.date_to || '')}`),
        staleTime: 60 * 1000,
    });
    const chauffeurFilterOptions = facetsQuery.data?.chauffeurs || [];
    const customerFilterOptions = facetsQuery.data?.customers || [];
    const queryStringBase = buildQueryString(filters);
    const queryString = isOwner
        ? `${queryStringBase}&include_totals=true`
        : queryStringBase;
    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['bookings', 'list', queryString],
        queryFn:  () => api.get(`/bookings?${queryString}`),
        keepPreviousData: true,
    });

    // Detect whether filters are narrowed from default state. Used to conditionally
    // show a "Reset" affordance — pointless to offer reset when nothing's filtered.
    // Default state = today preset, all statuses checked, all text/dropdown fields empty,
    // journey type 'any', default sort.
   const isNarrowed = (
                filters.date_preset !== 'today' ||
        filters.status.size > 0 ||
        Boolean(filters.booking_ref) ||
        Boolean(filters.invoice_number) ||
        Boolean(filters.customer_search) ||
        Boolean(filters.customer_id) ||
        Boolean(filters.chauffeur_id) ||
        Boolean(filters.vehicle_id) ||
        Boolean(filters.vehicle_class) ||
        Boolean(filters.pickup_area) ||
        Boolean(filters.dropoff_area) ||
        Boolean(filters.passenger_name) ||
        Boolean(filters.passenger_phone) ||
        (filters.journey_type && filters.journey_type !== 'any') ||
        (filters.invoice_status && filters.invoice_status !== 'all') ||
        Boolean(filters.search) ||
        (filters.sort && filters.sort !== 'pickup_asc')
    );

    // Bulk actions need a *coherent* scope, not just a date. A date alone
    // returns a mix of customers/chauffeurs, so "allocate all" / "email
    // customer" is meaningless. Same as isNarrowed but WITHOUT the date
    // preset — bulk bar only shows once a real narrowing is applied.
      const hasBulkFilter = (
        filters.status.size > 0 ||
        Boolean(filters.booking_ref) ||
        Boolean(filters.invoice_number) ||
        Boolean(filters.customer_search) ||
        Boolean(filters.customer_id) ||
        Boolean(filters.chauffeur_id) ||
        Boolean(filters.vehicle_id) ||
        Boolean(filters.vehicle_class) ||
        Boolean(filters.pickup_area) ||
        Boolean(filters.dropoff_area) ||
        Boolean(filters.passenger_name) ||
        Boolean(filters.passenger_phone) ||
        (filters.journey_type && filters.journey_type !== 'any') ||
        (filters.invoice_status && filters.invoice_status !== 'all') ||
        Boolean(filters.search)
    );

    // IMPORTANT: never early-return based on isLoading/isError. The page shell
    // (sidebar + filter inputs) must always be rendered so the inputs stay
    // mounted across refetches. Loading and error states are shown inside the
    // results area only, leaving the filter inputs untouched. This is the same
    // pattern the working ChauffeursListPage uses.
    const bookings = data?.bookings || [];
    const total    = data?.total ?? 0;
    const limit    = data?.limit ?? 50;
    const offset   = data?.offset ?? 0;
    const pageStart = total === 0 ? 0 : offset + 1;
    const pageEnd   = Math.min(offset + bookings.length, total);

    const toggleStatus = (s) => {
        const next = new Set(filters.status);
        if (next.has(s)) next.delete(s);
        else next.add(s);
        updateFilters({ status: next, offset: 0 });
    };

    const goToBooking = (id) => {
        // Slice 7d.2.2 will provide the detail page. For now this navigates to the
        // existing /bookings/:id route which will 404 — placeholder behaviour.
        navigate(`/bookings/${id}`);
    };

    // ---- Bulk operations (global allocate / email on the filtered set) -------
    // The action bar appears when isNarrowed is true (any filter beyond the
    // bare 'today' default). It operates on the booking IDs currently on
    // screen — for a filtered week that's the whole set (single page). The
    // count shown on each button IS the safety: you see exactly how many
    // bookings/recipients before firing.
    const bookingIds = useMemo(() => bookings.map((b) => b.id), [bookings]);
    const [bulkModal, setBulkModal] = useState(null); // 'allocate' | 'email_customer' | 'email_chauffeur' | null

    // Email-customer is only coherent when the filter resolves to ONE customer
    // (otherwise "who is this going to?" has no single answer).
    const singleCustomer = Boolean(filters.customer_id);

    const closeBulkModal = () => setBulkModal(null);

    return (
        <div className="min-h-screen bg-canvas">
            <PageHeader
                title="Bookings"
                subtitle={`${total.toLocaleString()} total · showing ${pageStart}–${pageEnd}`}
            />

            {/* Action row — Bookings|Quotes toggle on the left, "New booking" CTA
                on the right. Toggle lets dispatcher flip between this list and the
                quotes list without going through the sidebar. */}
            <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 pt-6 flex items-center justify-between gap-3 flex-wrap">
                                <ListModeToggle current="bookings" />

                <div className="flex items-center gap-2">
                <PrintBookingsButton
                    buildQueryString={buildQueryString}
                    filters={filters}
                    isOwner={isOwner}
                    statusLabels={STATUS_LABELS}
                />
                <Link
                    to="/bookings/new"
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 active:bg-emerald-800 transition-colors shadow-sm"
                >
                    <Plus className="w-4 h-4" />
                                        New booking
                </Link>
                </div>
            </div>

            {/* Bulk action bar — only when a filter is active (isNarrowed).
                Operates on the bookings currently listed. */}
            {hasBulkFilter && bookings.length > 0 && (
                <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 pt-4">
                    <div className="flex flex-wrap items-center gap-2 bg-ink/5 border border-line rounded-lg px-4 py-3">
                        <span className="text-sm text-ink font-medium mr-1">
                            {bookings.length} booking{bookings.length === 1 ? '' : 's'} listed
                        </span>
                        <span className="text-xs text-muted mr-2">Bulk actions:</span>

                        <button
                            type="button"
                            onClick={() => setBulkModal('allocate')}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-ink text-white text-sm font-medium hover:bg-ink/90"
                        >
                            <User className="w-3.5 h-3.5" />
                            Allocate all
                        </button>

                        <button
                            type="button"
                            onClick={() => setBulkModal('email_chauffeur')}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-line bg-white text-ink text-sm font-medium hover:border-ink"
                        >
                            Email chauffeur
                        </button>

                        <button
                            type="button"
                            onClick={() => singleCustomer && setBulkModal('email_customer')}
                            disabled={!singleCustomer}
                            title={singleCustomer ? '' : 'Filter to a single customer to email the customer'}
                            className={cn(
                                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm font-medium',
                                singleCustomer
                                    ? 'border-line bg-white text-ink hover:border-ink'
                                    : 'border-line bg-canvas text-muted cursor-not-allowed'
                            )}
                        >
                            Email customer
                        </button>
                    </div>
                </div>
            )}

            <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-6 grid lg:grid-cols-[280px_1fr] gap-6">
                {/* ----- Filter sidebar (desktop only) -----
                     On mobile we use a compact inline filter bar instead — see
                     <MobileFilterBar /> rendered above the results card. */}
                <aside className="hidden lg:block">
                    <div className="bg-white border border-line rounded-xl p-5 space-y-5">
                        <h2 className="text-sm font-semibold text-ink">Filters</h2>

                        {/* Date range */}
                        <div className="space-y-2">
                            <label className="block text-xs font-medium text-muted uppercase tracking-wide">Date range</label>
                            <div className="space-y-1">
                                {DATE_PRESETS.map(p => (
                                    <label key={p.value} className="flex items-center gap-2 cursor-pointer text-sm">
                                        <input
                                            type="radio"
                                            name="date_preset"
                                            checked={filters.date_preset === p.value}
                                            onChange={() => {
                                                const { from, to } = presetToDates(p.value);
                                                updateFilters({ date_preset: p.value, date_from: from, date_to: to, offset: 0 });
                                            }}
                                            className="text-ink focus:ring-ink"
                                        />
                                        <span className="text-ink">{p.label}</span>
                                    </label>
                                ))}
                               <div className="pt-2 space-y-1.5">
                                    <label className="block text-xs text-muted">Custom range</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="date"
                                            value={filters.date_from}
                                            onChange={(e) => setFromDate(e.target.value, filters, updateFilters)}
                                            className="flex-1 min-w-0 text-xs px-2 py-1.5 border border-line rounded"
                                        />
                                        <input
                                            type="date"
                                            value={filters.date_to}
                                            onChange={(e) => setToDate(e.target.value, filters, updateFilters)}
                                            className="flex-1 min-w-0 text-xs px-2 py-1.5 border border-line rounded"
                                        />
                                    </div>
                                    {/* Arrows centered below — shift both dates by 1 day in the chosen direction */}
                                    <div className="flex justify-center gap-1">
                                        <button
                                            type="button"
                                            onClick={() => shiftDateWindow(-1, filters, updateFilters)}
                                            disabled={!filters.date_from || !filters.date_to}
                                            className="px-3 py-1 rounded border border-line hover:bg-canvas disabled:opacity-30 disabled:cursor-not-allowed"
                                            aria-label="Shift dates one day earlier"
                                            title="Shift dates one day earlier"
                                        >
                                            <ChevronLeft className="h-3.5 w-3.5" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => shiftDateWindow(1, filters, updateFilters)}
                                            disabled={!filters.date_from || !filters.date_to}
                                            className="px-3 py-1 rounded border border-line hover:bg-canvas disabled:opacity-30 disabled:cursor-not-allowed"
                                            aria-label="Shift dates one day later"
                                            title="Shift dates one day later"
                                        >
                                            <ChevronRight className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Status */}
                        <div className="space-y-2">
                            <label className="block text-xs font-medium text-muted uppercase tracking-wide">Status</label>
                            <div className="space-y-1">
                                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                                    <label key={key} className="flex items-center gap-2 cursor-pointer text-sm">
                                        <input
                                            type="checkbox"
                                            checked={filters.status.has(key)}
                                            onChange={() => toggleStatus(key)}
                                            className="text-ink focus:ring-ink"
                                        />
                                        <span className="text-ink">{cfg.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Search inputs */}
                        <div className="space-y-3">
                            <FilterInput
                                                                label="Booking ref"
                                placeholder="CTS260426..."
                                value={filters.booking_ref}
                                onChange={(v) => updateFilters({ booking_ref: v, offset: 0 })}
                            />
                            <FilterInput
                                label="Invoice number"
                                placeholder="e.g. INV-1042"
                                value={filters.invoice_number}
                                onChange={(v) => updateFilters({ invoice_number: v, offset: 0 })}
                            />

                            <FilterInput
                                label="Customer"
                                placeholder="Name, company or email"
                                value={filters.customer_search}
                                onChange={(v) => updateFilters({ customer_search: v, offset: 0 })}
                            />
                            <FilterInput
                                label="Passenger name"
                                placeholder="e.g. Stefanek"
                                value={filters.passenger_name}
                                onChange={(v) => updateFilters({ passenger_name: v, offset: 0 })}
                            />
                            <FilterInput
                                label="Passenger phone"
                                placeholder="07... or partial"
                                value={filters.passenger_phone}
                                onChange={(v) => updateFilters({ passenger_phone: v, offset: 0 })}
                            />
                            <FilterInput
                                label="Pickup area"
                                placeholder="W1, SE1, EC2..."
                                value={filters.pickup_area}
                                onChange={(v) => updateFilters({ pickup_area: v, offset: 0 })}
                            />
                            <FilterInput
                                label="Dropoff area"
                                placeholder="W1, SE1, EC2..."
                                value={filters.dropoff_area}
                                onChange={(v) => updateFilters({ dropoff_area: v, offset: 0 })}
                            />
                        </div>

                       {/* Customer — scoped to the selected date window */}
                        <div className="space-y-1">
                            <label className="block text-xs font-medium text-muted uppercase tracking-wide">Customer</label>
                            <select
                                value={filters.customer_id}
                                onChange={(e) => updateFilters({ customer_id: e.target.value, offset: 0 })}
                                className="w-full text-sm px-2 py-1.5 border border-line rounded bg-white"
                            >
                                <option value="">Any customer</option>
                                {customerFilterOptions.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Chauffeur */}
                        <div className="space-y-1">
                            <label className="block text-xs font-medium text-muted uppercase tracking-wide">Chauffeur</label>
                            <select
                                value={filters.chauffeur_id}
                                onChange={(e) => updateFilters({ chauffeur_id: e.target.value, offset: 0 })}
                                className="w-full text-sm px-2 py-1.5 border border-line rounded bg-white"
                            >
                                <option value="">Any chauffeur</option>
                                {chauffeurFilterOptions.map(c => (
                                    <option key={c.id} value={c.id}>
                                        {c.first_name} {c.last_name}
                                        {c.call_sign ? ` · ${c.call_sign}` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Vehicle (specific car) */}
                        <div className="space-y-1">
                            <label className="block text-xs font-medium text-muted uppercase tracking-wide">Vehicle</label>
                            <select
                                value={filters.vehicle_id}
                                onChange={(e) => updateFilters({ vehicle_id: e.target.value, offset: 0 })}
                                className="w-full text-sm px-2 py-1.5 border border-line rounded bg-white"
                            >
                                <option value="">Any vehicle</option>
                                {vehicleOptions.map(v => (
                                    <option key={v.id} value={v.id}>
                                        {v.registration}
                                        {v.nickname ? ` · ${v.nickname}` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Vehicle class */}
                        <div className="space-y-1">
                            <label className="block text-xs font-medium text-muted uppercase tracking-wide">Vehicle class</label>
                            <select
                                value={filters.vehicle_class}
                                onChange={(e) => updateFilters({ vehicle_class: e.target.value, offset: 0 })}
                                className="w-full text-sm px-2 py-1.5 border border-line rounded bg-white"
                            >
                                {VEHICLE_CLASSES.map(c => (
                                    <option key={c.value} value={c.value}>{c.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Journey type */}
                        <div className="space-y-2">
                            <label className="block text-xs font-medium text-muted uppercase tracking-wide">Journey type</label>
                            <div className="flex gap-3 text-sm">
                                {[{ v: 'any', l: 'Any' }, { v: 'oneway', l: 'One-way' }, { v: 'hourly', l: 'Hourly' }].map(opt => (
                                    <label key={opt.v} className="flex items-center gap-1.5 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="journey_type"
                                            checked={filters.journey_type === opt.v}
                                            onChange={() => updateFilters({ journey_type: opt.v, offset: 0 })}
                                            className="text-ink focus:ring-ink"
                                        />
                                        <span className="text-ink">{opt.l}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Invoice status (customer side) */}
                        <div className="space-y-1">
                            <label className="block text-xs font-medium text-muted uppercase tracking-wide">Invoice</label>
                            <select
                                value={filters.invoice_status || 'all'}
                                onChange={(e) => updateFilters({ invoice_status: e.target.value, offset: 0 })}
                                className="w-full text-sm px-2 py-1.5 border border-line rounded bg-white"
                            >
                                <option value="all">All</option>
                                <option value="not_invoiced">Not invoiced</option>
                                <option value="invoiced">Invoiced</option>
                            </select>
                        </div>

                        <button
                            type="button"
                            onClick={resetFilters}
                            className="w-full text-sm text-muted hover:text-ink underline"
                        >
                            Clear all filters
                        </button>
                    </div>
                </aside>

                {/* ----- Results table ----- */}
                <div className="bg-white border border-line rounded-xl overflow-hidden">
                    {/* Mobile-only filter bar — date pills + status popover.
                         Sits above the search bar; hidden on desktop where the
                         full sidebar handles all filtering. */}
                    <MobileFilterBar
                        filters={filters}
                        updateFilters={updateFilters}
                        isNarrowed={isNarrowed}
                        resetFilters={resetFilters}
                    />

                    {/* Top bar with search + sort */}
                    <div className="px-4 py-3 border-b border-line flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted pointer-events-none" />
                            <SearchInput
                                value={filters.search}
                                onChange={(v) => updateFilters({ search: v, offset: 0 })}
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <label className="text-xs text-muted">Sort:</label>
                            <select
                                value={filters.sort}
                                onChange={(e) => updateFilters({ sort: e.target.value, offset: 0 })}
                                className="text-sm px-2 py-1.5 border border-line rounded bg-white"
                            >
                                <option value="pickup_asc">Soonest pickup first</option>
                                <option value="pickup_desc">Latest pickup first</option>
                                <option value="created_desc">Recently created</option>
                            </select>
                        </div>
                    </div>

                    {/* Results — hybrid layout: dense two-line rows on desktop,
                         spacious cards on mobile. Same data either way.
                         Loading and error states render inline (NEVER as full-page
                         returns) so the filter inputs stay mounted. */}
                    {isLoading && !data ? (
                        <div className="px-6 py-16 text-center text-muted text-sm">
                            Loading bookings…
                        </div>
                    ) : isError && !data ? (
                        <div className="px-6 py-16 text-center">
                            <div className="text-sm text-red-600">{error?.message || 'Could not load bookings'}</div>
                        </div>
                    ) : bookings.length === 0 ? (
                        <div className="px-6 py-16 text-center">
                            <div className="text-muted text-sm">No bookings match these filters.</div>
                            <button
                                type="button"
                                onClick={resetFilters}
                                className="mt-3 text-sm text-ink underline"
                            >
                                Clear filters
                            </button>
                        </div>
                    ) : (
                        <ul className="divide-y divide-line">
                            {bookings.map(b => (
                           <BookingRow key={b.id} booking={b} isOwner={isOwner} onClick={() => goToBooking(b.id)} />
                            ))}
                        </ul>
                    )}

                    {/* Pagination */}
                    {total > limit && (
                        <div className="px-4 py-3 border-t border-line flex items-center justify-between">
                            <div className="text-xs text-muted">
                                Showing {pageStart}–{pageEnd} of {total.toLocaleString()}
                            </div>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    disabled={offset === 0}
                                    onClick={() => updateFilters({ offset: Math.max(0, offset - limit) })}
                                    className="p-2 rounded hover:bg-canvas disabled:opacity-40 disabled:cursor-not-allowed"
                                    aria-label="Previous page"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </button>
                                <button
                                    type="button"
                                    disabled={pageEnd >= total}
                                    onClick={() => updateFilters({ offset: offset + limit })}
                                    className="p-2 rounded hover:bg-canvas disabled:opacity-40 disabled:cursor-not-allowed"
                                    aria-label="Next page"
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Totals bar — owner-only quick-check across the filtered set.
                        Sums customer + chauffeur billing line gross. Cross-checks
                        against statement totals when filtering one chauffeur for
                        a date range — same data, different view, must match.
                        Hidden when no bookings or non-owner. */}
                    {isOwner && data?.totals && total > 0 && (
                        <TotalsBar totals={data.totals} bookingCount={total} />
                    )}
                </div>
            </div>

            {/* Bulk modals — rendered at page level so they overlay everything */}
            {bulkModal === 'allocate' && (
                <BulkAllocateModal
                    bookingIds={bookingIds}
                    chauffeurOptions={chauffeurOptions}
                    vehicleOptions={vehicleOptions}
                    onClose={closeBulkModal}
                    onDone={() => { closeBulkModal(); queryClient.invalidateQueries({ queryKey: ['bookings', 'list'] }); }}
                />
            )}
            {bulkModal === 'email_customer' && (
                <BulkEmailModal
                    audience="customer"
                    bookingIds={bookingIds}
                    onClose={closeBulkModal}
                    onDone={() => { closeBulkModal(); queryClient.invalidateQueries({ queryKey: ['bookings', 'list'] }); }}
                />
            )}
            {bulkModal === 'email_chauffeur' && (
                <BulkEmailModal
                    audience="chauffeur"
                    bookingIds={bookingIds}
                    onClose={closeBulkModal}
                    onDone={() => { closeBulkModal(); queryClient.invalidateQueries({ queryKey: ['bookings', 'list'] }); }}
                />
            )}
        </div>
    );
}

// =============================================================================
// TotalsBar — quick-check totals for the current filter
// =============================================================================
// Customer + Chauffeur + Margin + coverage indicator. Cross-checks against
// statement totals when narrowed to one chauffeur over a date range —
// same underlying data, different view, must agree.
function TotalsBar({ totals, bookingCount }) {
    // 7e net-totals: backend returns net (ex VAT) values matching the stats
    // page convention. Costs added — operating costs are company-side
    // internal spend, subtracted from margin alongside chauffeur cost.
    const customer = Number(totals.customer_net) || 0;
    const chauffeur = Number(totals.chauffeur_net) || 0;
    const costs = Number(totals.costs_net) || 0;
    const margin = customer - chauffeur - costs;
    const marginPct = customer > 0 ? (margin / customer) * 100 : 0;
    const coverage = totals.bookings_with_billing || 0;
    const coverageMissing = bookingCount - coverage;

    return (
        <div className="border-t-2 border-ink/80 bg-canvas/30 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
                <div>
                    <span className="text-xs text-muted uppercase tracking-wide">Customer </span>
                    <span className="font-semibold text-ink">£{customer.toFixed(2)}</span>
                    <span className="text-[10px] text-muted ml-1">ex VAT</span>
                </div>
                <div>
                    <span className="text-xs text-muted uppercase tracking-wide">Chauffeur </span>
                    <span className="font-semibold text-ink">£{chauffeur.toFixed(2)}</span>
                    <span className="text-[10px] text-muted ml-1">ex VAT</span>
                </div>
                <div>
                    <span className="text-xs text-muted uppercase tracking-wide">Costs </span>
                    <span className="font-semibold text-ink">£{costs.toFixed(2)}</span>
                    <span className="text-[10px] text-muted ml-1">ex VAT</span>
                </div>
                <div>
                    <span className="text-xs text-muted uppercase tracking-wide">Margin </span>
                    <span className={`font-semibold ${margin >= 0 ? 'text-ink' : 'text-red-700'}`}>
                        £{margin.toFixed(2)}
                    </span>
                    {customer > 0 && (
                        <span className="text-xs text-muted ml-1">
                            ({marginPct.toFixed(1)}%)
                        </span>
                    )}
                </div>
            </div>
            <div className="text-xs text-muted">
                {coverageMissing > 0
                    ? `${coverage} of ${bookingCount} have billing data`
                    : `${bookingCount} booking${bookingCount !== 1 ? 's' : ''}`}
            </div>
        </div>
    );
}

// =============================================================================
// MobileFilterBar
// =============================================================================
// Compact filter row shown above the search bar on mobile. Replaces the
// drawer entirely. Two controls only: date pills (Today/Tomorrow/This week)
// and a status pill that opens a popover with the 8 status checkboxes. All
// other filters (text inputs, vehicle class, journey type) are desktop-only.
//
// Rationale: dispatch is desktop-first. Mobile is mostly "look something up"
// (handled by the search bar) plus the two genuinely useful narrowings:
// what's today/tomorrow/this week, and what's still unallocated.
function MobileFilterBar({ filters, updateFilters, isNarrowed, resetFilters }) {
    const [statusOpen, setStatusOpen] = useState(false);
    const statusCount = filters.status.size;
   // Show count badge only when statuses are explicitly selected.
    // Default state (no statuses) = no badge.
    const statusBadge = statusCount > 0 ? statusCount : null;

    const pickPreset = (presetValue) => {
        const { from, to } = presetToDates(presetValue);
        updateFilters({ date_preset: presetValue, date_from: from, date_to: to, offset: 0 });
    };

    const toggleStatus = (s) => {
        const next = new Set(filters.status);
        if (next.has(s)) next.delete(s);
        else next.add(s);
        updateFilters({ status: next, offset: 0 });
    };

   return (
        <div className="lg:hidden px-4 py-3 border-b border-line space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {/* Date preset pills */}
            {MOBILE_DATE_PRESETS.map(p => {
                const active = filters.date_preset === p.value;
                return (
                    <button
                        key={p.value}
                        type="button"
                        onClick={() => pickPreset(p.value)}
                        className={cn(
                            'px-3 py-1.5 text-sm rounded-full border transition-colors',
                            active
                                ? 'bg-ink text-white border-ink'
                                : 'bg-white text-ink border-line hover:border-ink'
                        )}
                    >
                        {p.label}
                    </button>
                );
            })}

            {/* Status popover button */}
            <div className="relative">
                <button
                    type="button"
                    onClick={() => setStatusOpen(o => !o)}
                    className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full border transition-colors',
                        statusBadge !== null
                            ? 'bg-ink text-white border-ink'
                            : 'bg-white text-ink border-line hover:border-ink'
                    )}
                    aria-expanded={statusOpen}
                    aria-haspopup="true"
                >
                    Status
                    {statusBadge !== null && (
                        <span className="text-xs opacity-80">({statusBadge})</span>
                    )}
                </button>

                {statusOpen && (
                    <>
                        {/* Backdrop catches outside taps to dismiss */}
                        <button
                            type="button"
                            onClick={() => setStatusOpen(false)}
                            aria-label="Close status filter"
                            className="fixed inset-0 z-30 bg-transparent cursor-default"
                        />
                        <div className="absolute z-40 left-0 mt-2 w-56 bg-white border border-line rounded-lg shadow-lg p-3 space-y-1.5">
                            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                                <label key={key} className="flex items-center gap-2 cursor-pointer text-sm py-0.5">
                                    <input
                                        type="checkbox"
                                        checked={filters.status.has(key)}
                                        onChange={() => toggleStatus(key)}
                                        className="text-ink focus:ring-ink"
                                    />
                                    <span className="text-ink">{cfg.label}</span>
                                </label>
                            ))}
                        </div>
                    </>
                )}
            </div>

{/* Reset — inline with the pills so the action sits next to what it
                affects, not floating up by the page header. Muted styling (no
                pill border, no fill) signals "undo" rather than "another filter
                option." Only renders when at least one filter has been narrowed. */}
            {isNarrowed && (
                <button
                    type="button"
                    onClick={resetFilters}
                    className="inline-flex items-center gap-1 px-2 py-1.5 text-sm text-muted hover:text-ink"
                    aria-label="Clear all filters"
                >
                    <X className="h-3.5 w-3.5" />
                    Reset
                </button>
            )}
          </div>

          {/* Custom date range — from/to native pickers + day-shift arrows.
              Native inputs trigger the phone's date picker; nothing custom to
              style. Setting either date clears the active preset so the pills
              deselect. Mirrors the desktop sidebar's custom-range control. */}
          <div className="flex items-center gap-2">
                <input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => setFromDate(e.target.value, filters, updateFilters)}
                    className="flex-1 min-w-0 text-sm px-2 py-1.5 border border-line rounded bg-white"
                    aria-label="From date"
                />
                <span className="text-muted text-sm">–</span>
                <input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => setToDate(e.target.value, filters, updateFilters)}
                    className="flex-1 min-w-0 text-sm px-2 py-1.5 border border-line rounded bg-white"
                    aria-label="To date"
                />
                <button
                    type="button"
                    onClick={() => shiftDateWindow(-1, filters, updateFilters)}
                    disabled={!filters.date_from || !filters.date_to}
                    className="px-2.5 py-1.5 rounded border border-line hover:bg-canvas disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label="Shift dates one day earlier"
                >
                    <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                    type="button"
                    onClick={() => shiftDateWindow(1, filters, updateFilters)}
                    disabled={!filters.date_from || !filters.date_to}
                    className="px-2.5 py-1.5 rounded border border-line hover:bg-canvas disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label="Shift dates one day later"
                >
                    <ChevronRight className="h-4 w-4" />
                </button>
          </div>
        </div>
    );
}

// Top-of-page search input. Self-debounced like FilterInput, but with the
// search-bar styling (full width, rounded, room for the search icon to its left).
function SearchInput({ value, onChange }) {
    const [local, setLocal] = useState(value || '');
    useEffect(() => {
        if ((value || '') !== local) setLocal(value || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    useEffect(() => {
        if ((local || '') === (value || '')) return;
        const t = setTimeout(() => onChange(local), 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [local]);

    return (
        <input
            type="search"
            placeholder="Search across all fields…"
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-line rounded-lg focus:outline-none focus:border-ink"
        />
    );
}

// Small reusable filter input component for the sidebar.
//
// Self-debounced and self-owned: the <input> keeps its own local state and
// only propagates upward 300ms after typing stops. This decouples it entirely
// from the parent's render cycle — when the booking list refetches and the
// parent re-renders, this component's input element does NOT get its `value`
// prop touched, so React's reconciliation has zero reason to disturb it.
// Cursor stays put no matter what happens upstream.
//
// External changes to `value` (e.g. user clicks "Clear all filters") are
// detected via useEffect and synced into the local state.
function FilterInput({ label, placeholder, value, onChange }) {
    const [local, setLocal] = useState(value || '');

    // Sync external changes (reset button, URL hydration) into local state.
    // We compare against `local` to avoid re-syncing when the change came from
    // us (which would create an infinite loop).
    useEffect(() => {
        if ((value || '') !== local) {
            setLocal(value || '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    // Debounce local → onChange. When user stops typing for 300ms, propagate.
    useEffect(() => {
        if ((local || '') === (value || '')) return; // already in sync, no-op
        const t = setTimeout(() => onChange(local), 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [local]);

    return (
        <div className="space-y-1">
            <label className="block text-xs font-medium text-muted uppercase tracking-wide">{label}</label>
            <input
                type="text"
                value={local}
                onChange={(e) => setLocal(e.target.value)}
                placeholder={placeholder}
                className="w-full text-sm px-2 py-1.5 border border-line rounded focus:outline-none focus:border-ink"
            />
        </div>
    );
}

// Invoice pill — shown next to the operational status pill when a booking
// has been invoiced. Single state ("Invoiced"); absence of pill means
// not yet invoiced. Customer billing status is orthogonal to operational
// status — a 'completed' booking may or may not be invoiced; a 'cancelled'
// booking with a cancellation fee may also end up invoiced.
function InvoicePill({ billingStatus }) {
    if (billingStatus !== 'invoiced') return null;
    return (
        <span
            className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium"
            style={{ backgroundColor: '#EEEDFE', color: '#3C3489' }}
        >
            Invoiced
        </span>
    );
}

// Render a single booking. Hybrid layout:
//   - lg+ (desktop): dense two-line row with [pickup | meta·route·vehicle | status]
//     This is what dispatchers see at the desk — high density, scannable.
//   - <lg (tablet/phone): spacious card with stacked sections
//     This is what owner sees on mobile — readable, no horizontal squeeze.
// Same data either way; only the visual structure differs.
function BookingRow({ booking: b, isOwner, onClick }) {
    const statusConfig = STATUS_CONFIG[b.status] || { label: b.status, pillStatus: 'neutral' };
    const passenger = b.passenger_name || b.pax_name;
    const vehicle   = b.vehicle_class_label || b.vehicle_class || '—';
    const pickupFmt = formatPickup(b.pickup_date, b.pickup_time);
    const customerNet = b.customer_net != null ? Number(b.customer_net) : null;
    const customerNetStr = customerNet != null && customerNet > 0 ? `£${customerNet.toFixed(2)}` : null;

    return (
        <li
            onClick={onClick}
            className="cursor-pointer hover:bg-canvas/60 transition-colors"
        >
            {/* ---- Desktop: dense two-line row (Option D) ---- */}
           <div className="hidden lg:grid grid-cols-[150px_1fr_110px] gap-4 px-4 py-3 items-start">
                {/* Pickup column */}
                <div>
                    <div className="font-medium text-ink text-sm whitespace-nowrap">{pickupFmt}</div>
                    {b.mode === 'hourly' && b.final_time && (
                        <div className="text-xs text-muted">until {b.final_time}</div>
                    )}
                </div>

                {/* Middle: 3 stacked lines of detail */}
                <div className="min-w-0 space-y-1">
                    {/* Line 1: ref · customer · passenger */}
                    <div className="flex items-center gap-2 text-sm flex-wrap">
                        <span className="font-mono text-xs text-muted">{b.ride_ref}</span>
                        <span className="text-muted">·</span>
                        <span className="text-ink truncate">{b.customer_name || '—'}</span>
                        {passenger && (
                            <>
                                <span className="text-muted">·</span>
                                <span className="text-muted truncate">Pax: {passenger}</span>
                            </>
                        )}
                    </div>
                    {/* Line 2: route */}
                    <div className="text-sm text-ink truncate" title={`${b.pickup_address} → ${b.dropoff_address}`}>
                        {b.pickup_address} <span className="text-muted">→</span> {b.dropoff_address}
                    </div>
                    {/* Line 3: vehicle class · chauffeur · reg */}
                    <div className="flex items-center gap-2 text-xs text-muted flex-wrap">
                        <span>{vehicle}</span>
                        <span>·</span>
                        {b.chauffeur_name ? (
                            <>
                                <span className="text-ink">{b.chauffeur_name}</span>
                                {b.vehicle_registration && (
                                    <>
                                        <span>·</span>
                                        <span>{b.vehicle_registration}</span>
                                    </>
                                )}
                            </>
                        ) : (
                            <span className="italic">Not allocated</span>
                        )}
                    </div>
                                        {isOwner && customerNetStr && (
                        <div className="text-xs">
                            <span className="text-muted">Customer </span>
                            <span className="font-semibold text-ink">{customerNetStr}</span>
                            <span className="text-[10px] text-muted ml-1">ex VAT</span>
                        </div>
                    )}
                </div>

                {/* Status + invoice pills — right-aligned. Two pills when
                    invoiced, one when not. Pills wrap to next line on narrow
                    desktops if both don't fit (rare given fixed 110px column —
                    may need to widen the grid template if it gets cramped). */}
                <div className="flex justify-end items-center gap-1.5 flex-wrap">
                    <StatusPill status={statusConfig.pillStatus}>{statusConfig.label}</StatusPill>
                    <InvoicePill billingStatus={b.customer_billing_status} />
                </div>
            </div>

            {/* ---- Mobile/tablet: spacious card (Option A) ---- */}
            <div className="lg:hidden p-4 space-y-3">
                {/* Header: pickup + status */}
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="font-medium text-ink">{pickupFmt}</div>
                        {b.mode === 'hourly' && b.final_time && (
                            <div className="text-xs text-muted">until {b.final_time}</div>
                        )}
                        <div className="font-mono text-xs text-muted mt-0.5">{b.ride_ref}</div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap justify-end">
                        <StatusPill status={statusConfig.pillStatus}>{statusConfig.label}</StatusPill>
                        <InvoicePill billingStatus={b.customer_billing_status} />
                    </div>
                </div>
                {/* Customer + passenger */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                        <div className="text-xs uppercase text-muted tracking-wide mb-0.5">Customer</div>
                        <div className="text-ink">{b.customer_name || '—'}</div>
                    </div>
                    <div>
                        <div className="text-xs uppercase text-muted tracking-wide mb-0.5">Passenger</div>
                        <div className="text-ink">{passenger || <span className="text-muted">—</span>}</div>
                    </div>
                </div>

                {/* Route */}
                <div>
                    <div className="text-xs uppercase text-muted tracking-wide mb-0.5">Route</div>
                    <div className="text-sm text-ink">{b.pickup_address}</div>
                    <div className="text-sm text-muted">↓ {b.dropoff_address}</div>
                </div>

                {/* Vehicle + chauffeur */}
                <div>
                    <div className="text-xs uppercase text-muted tracking-wide mb-0.5">Vehicle · Chauffeur</div>
                    <div className="text-sm text-ink">
                        {vehicle}
                        {' · '}
                        {b.chauffeur_name || <span className="text-muted italic">Not allocated</span>}
                        {b.vehicle_registration && <span className="text-muted"> · {b.vehicle_registration}</span>}
                    </div>
                </div>
            </div>
        </li>
    );
}

// =============================================================================
// BulkAllocateModal — allocate one chauffeur (+ optional vehicle) to N bookings
// =============================================================================
// Reuses the chauffeur/vehicle option arrays already fetched for the sidebar
// filters. Posts to /bookings/bulk/allocate. Handles the 422 compliance_failed
// response by surfacing the issues + an owner-only override field. Shows a
// result summary (allocated / failed) after firing.
function BulkAllocateModal({ bookingIds, chauffeurOptions, vehicleOptions, onClose, onDone }) {
    const api = useApi();
    const { isOwner } = useCurrentUser();
    const [chauffeurId, setChauffeurId] = useState('');
    const [vehicleId, setVehicleId] = useState('');
    const [overrideReason, setOverrideReason] = useState('');
    const [compliance, setCompliance] = useState(null); // {chauffeur, vehicle} on 422
    const [result, setResult] = useState(null);         // {allocated, failed, ...}

    const mutation = useMutation({
        mutationFn: (body) => api.post('/bookings/bulk/allocate', body),
        onSuccess: (data) => { setResult(data); setCompliance(null); },
        onError: (err) => {
            // 422 compliance_failed — ApiError carries the parsed JSON body on
            // err.response (see lib/api.js). The body is { error, chauffeur, vehicle }.
            const payload = err?.response || null;
            if (payload?.error === 'compliance_failed' || err?.statusCode === 422) {
                setCompliance({
                    chauffeur: payload?.chauffeur || null,
                    vehicle: payload?.vehicle || null,
                });
            }
        },
    });

    const fire = () => {
        const body = { booking_ids: bookingIds, chauffeur_id: chauffeurId };
        if (vehicleId) body.vehicle_id = vehicleId;
        if (overrideReason.trim()) body.override_reason = overrideReason.trim();
        mutation.mutate(body);
    };

    return (
        <ModalShell title={`Allocate ${bookingIds.length} booking${bookingIds.length === 1 ? '' : 's'}`} onClose={onClose}>
            {result ? (
                <BulkResult
                    okLabel="allocated"
                    ok={result.allocated}
                    failed={result.failed}
                    onClose={onDone}
                />
            ) : (
                <div className="space-y-4">
                    <div className="space-y-1">
                        <label className="block text-xs font-medium text-muted uppercase tracking-wide">Chauffeur</label>
                        <select
                            value={chauffeurId}
                            onChange={(e) => setChauffeurId(e.target.value)}
                            className="w-full text-sm px-2 py-2 border border-line rounded bg-white"
                        >
                            <option value="">Select a chauffeur…</option>
                            {chauffeurOptions.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.first_name} {c.last_name}{c.call_sign ? ` · ${c.call_sign}` : ''}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="space-y-1">
                        <label className="block text-xs font-medium text-muted uppercase tracking-wide">Vehicle (optional)</label>
                        <select
                            value={vehicleId}
                            onChange={(e) => setVehicleId(e.target.value)}
                            className="w-full text-sm px-2 py-2 border border-line rounded bg-white"
                        >
                            <option value="">No vehicle</option>
                            {vehicleOptions.map((v) => (
                                <option key={v.id} value={v.id}>
                                    {v.registration}{v.nickname ? ` · ${v.nickname}` : ''}
                                </option>
                            ))}
                        </select>
                    </div>

                    {compliance && (
                        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2">
                            <div className="text-sm font-semibold text-amber-900">Compliance check failed</div>
                            {compliance.chauffeur && !compliance.chauffeur.ok && (
                                <div className="text-xs text-amber-800">
                                    Chauffeur: {(compliance.chauffeur.issues || []).join(', ') || 'failed'}
                                </div>
                            )}
                            {compliance.vehicle && !compliance.vehicle.ok && (
                                <div className="text-xs text-amber-800">
                                    Vehicle: {(compliance.vehicle.issues || []).join(', ') || 'failed'}
                                </div>
                            )}
                            {isOwner ? (
                                <div className="space-y-1 pt-1">
                                    <label className="block text-xs font-medium text-amber-900">Override reason (required to proceed)</label>
                                    <input
                                        type="text"
                                        value={overrideReason}
                                        onChange={(e) => setOverrideReason(e.target.value)}
                                        placeholder="Why allocate despite the failure?"
                                        className="w-full text-sm px-2 py-1.5 border border-amber-300 rounded"
                                    />
                                </div>
                            ) : (
                                <div className="text-xs text-amber-800 italic">An owner must override compliance failures.</div>
                            )}
                        </div>
                    )}

                    {mutation.isError && !compliance && (
                        <div className="text-sm text-red-600">
                            {mutation.error?.message || 'Allocation failed'}
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-muted hover:text-ink">
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={fire}
                            disabled={!chauffeurId || mutation.isPending || (compliance && isOwner && !overrideReason.trim())}
                            className="px-4 py-2 text-sm font-semibold rounded-md bg-ink text-white hover:bg-ink/90 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {mutation.isPending ? 'Allocating…' : `Allocate ${bookingIds.length}`}
                        </button>
                    </div>
                </div>
            )}
        </ModalShell>
    );
}

// =============================================================================
// BulkEmailModal — bulk customer OR chauffeur email, separate/consolidated
// =============================================================================
export function BulkEmailModal({ audience, bookingIds, onClose, onDone }) {
    const api = useApi();
        const [mode, setMode] = useState('separate');
    const [sendSms, setSendSms] = useState(false);
    const [result, setResult] = useState(null);

    const endpoint = audience === 'customer'
        ? '/bookings/bulk/email-customer'
        : '/bookings/bulk/email-chauffeur';

    const title = audience === 'customer'
        ? `Email customer · ${bookingIds.length} booking${bookingIds.length === 1 ? '' : 's'}`
        : `Email chauffeur · ${bookingIds.length} booking${bookingIds.length === 1 ? '' : 's'}`;

    const mutation = useMutation({
        mutationFn: (body) => api.post(endpoint, body),
        onSuccess: (data) => setResult(data),
    });

        const fire = () => mutation.mutate({ booking_ids: bookingIds, mode, send_sms: sendSms });

    const separateLabel = audience === 'customer'
        ? 'One email per booking (to each booker)'
        : 'One email per booking (with status link — also dispatches)';
    const consolidatedLabel = audience === 'customer'
        ? 'One email per customer listing all their bookings'
                : 'One email per chauffeur listing all their jobs (with status link — also dispatches)';

    return (
        <ModalShell title={title} onClose={onClose}>
            {result ? (
                <BulkResult
                    okLabel="sent"
                    ok={result.sent}
                    failed={result.skipped}
                    failedLabel="skipped"
                    onClose={onDone}
                />
            ) : (
                <div className="space-y-4">
                    <div className="space-y-2">
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input type="radio" name="mode" checked={mode === 'separate'} onChange={() => setMode('separate')} className="mt-0.5 text-ink focus:ring-ink" />
                            <span className="text-sm text-ink">{separateLabel}</span>
                        </label>
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input type="radio" name="mode" checked={mode === 'consolidated'} onChange={() => setMode('consolidated')} className="mt-0.5 text-ink focus:ring-ink" />
                            <span className="text-sm text-ink">{consolidatedLabel}</span>
                        </label>
                    </div>

                                        <label className="flex items-start gap-2 cursor-pointer pt-2 border-t border-paper-border-soft">
                        <input type="checkbox" checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} className="mt-0.5" />
                        <span className="text-sm text-ink">
                            Also send SMS
                            <span className="block text-xs text-ink-subtle">
                                {audience === 'customer'
                                    ? 'One text per customer: "X bookings confirmed. Full details in your email."'
                                    : 'One text per chauffeur with the portal link.'}
                            </span>
                        </span>
                    </label>

                    {mutation.isError && (
                        <div className="text-sm text-red-600">{mutation.error?.message || 'Send failed'}</div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-muted hover:text-ink">Cancel</button>
                        <button
                            type="button"
                            onClick={fire}
                            disabled={mutation.isPending}
                            className="px-4 py-2 text-sm font-semibold rounded-md bg-ink text-white hover:bg-ink/90 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {mutation.isPending ? 'Sending…' : 'Send'}
                        </button>
                    </div>
                </div>
            )}
        </ModalShell>
    );
}

// Bare modal shell — backdrop + centered card. No external dep.
function ModalShell({ title, onClose, children }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <button type="button" onClick={onClose} aria-label="Close" className="absolute inset-0 bg-black/40" />
            <div className="relative z-10 w-full max-w-md bg-white rounded-xl shadow-xl border border-line">
                <div className="flex items-center justify-between px-5 py-4 border-b border-line">
                    <h3 className="text-sm font-semibold text-ink">{title}</h3>
                    <button type="button" onClick={onClose} className="text-muted hover:text-ink"><X className="w-4 h-4" /></button>
                </div>
                <div className="px-5 py-4">{children}</div>
            </div>
        </div>
    );
}

// Result summary — N succeeded, list of failures/skips.
function BulkResult({ okLabel, ok = [], failed = [], failedLabel = 'failed', onClose }) {
    return (
        <div className="space-y-4">
            <div className="text-sm text-ink">
                <span className="font-semibold text-emerald-700">{ok.length} {okLabel}</span>
                {failed.length > 0 && (
                    <span className="text-muted"> · {failed.length} {failedLabel}</span>
                )}
            </div>
            {failed.length > 0 && (
                <div className="rounded-lg border border-line bg-canvas/40 p-3 max-h-48 overflow-y-auto space-y-1">
                    {failed.map((f, i) => (
                        <div key={i} className="text-xs text-muted">
                            <span className="font-mono text-ink">{f.ride_ref || f.id}</span> — {f.reason}
                        </div>
                    ))}
                </div>
            )}
            <div className="flex justify-end pt-1">
                <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold rounded-md bg-ink text-white hover:bg-ink/90">Done</button>
            </div>
        </div>
    );
}
